import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, ChevronDown, Check } from 'lucide-react';

interface LanguageSwitcherProps {
  className?: string;
  variant?: 'full' | 'icon' | 'minimal' | 'footer' | 'floating';
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  className = '', 
  variant = 'full' 
}) => {
  const { language, changeLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [animate, setAnimate] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);
  
  // Trigger animation when language changes
  const handleLanguageChange = (lang: string) => {
    if (lang === language) return;
    
    setAnimate(true);
    changeLanguage(lang);
    setIsOpen(false);
    
    // Reset animation after it completes
    setTimeout(() => {
      setAnimate(false);
    }, 700);
  };
  
  // Prevent click propagation when toggling dropdown
  const handleToggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChanged = () => {
      // Force re-render
      setAnimate(true);
      setTimeout(() => {
        setAnimate(false);
      }, 300);
    };

    window.addEventListener('language-changed', handleLanguageChanged);
    return () => {
      window.removeEventListener('language-changed', handleLanguageChanged);
    };
  }, []);

  // Floating variant (fixed position at bottom right)
  if (variant === 'floating') {
    return (
      <div className={`floating-language-button ${className}`} ref={dropdownRef}>
        <button
          onClick={handleToggleDropdown}
          className={`flex items-center px-3 py-2 rounded-full bg-white text-primary border border-gray-200 shadow-md hover:shadow-lg transition-all ${animate ? 'animate-pulse' : ''}`}
          aria-label={t('language.switch')}
        >
          <Globe size={18} className={`mr-1 ${animate ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">{t('language.label')}</span>
        </button>
        
        {isOpen && (
          <div className="absolute bottom-full right-0 mb-2 w-40 bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-100 animate-fadeIn">
            <div className="p-1">
              <button
                onClick={() => handleLanguageChange('es')}
                className={`w-full flex items-center px-4 py-3 text-sm text-left rounded-md language-option ${
                  language === 'es' 
                    ? 'bg-primary bg-opacity-10 font-medium text-primary' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-lg mr-2">🇪🇸</span>
                <span className="flex-grow">Español</span>
                {language === 'es' && <Check size={16} className="ml-auto text-primary" />}
              </button>
              
              <button
                onClick={() => handleLanguageChange('en')}
                className={`w-full flex items-center px-4 py-3 text-sm text-left rounded-md language-option ${
                  language === 'en' 
                    ? 'bg-primary bg-opacity-10 font-medium text-primary' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-lg mr-2">🇺🇸</span>
                <span className="flex-grow">English</span>
                {language === 'en' && <Check size={16} className="ml-auto text-primary" />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Footer variant (smaller, elegant)
  if (variant === 'footer') {
    return (
      <div className={`inline-block language-selector ${className}`} ref={dropdownRef}>
        <button
          onClick={handleToggleDropdown}
          className={`flex items-center px-3 py-1.5 rounded-lg bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm transition-all ${animate ? 'animate-pulse' : ''}`}
          aria-label={t('language.switch')}
        >
          <Globe size={14} className={`mr-1.5 ${animate ? 'animate-spin' : ''}`} />
          <span className="text-xs font-medium">{t('language.label')}</span>
          <ChevronDown size={12} className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute mt-1 w-32 bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-100 animate-fadeIn">
            <div className="p-1">
              <button
                onClick={() => handleLanguageChange('es')}
                className={`w-full flex items-center px-3 py-2 text-xs text-left rounded-md language-option ${
                  language === 'es' 
                    ? 'bg-primary text-white font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-base mr-2">🇪🇸</span>
                <span className="flex-grow">Español</span>
                {language === 'es' && <Check size={12} className="ml-auto" />}
              </button>
              
              <button
                onClick={() => handleLanguageChange('en')}
                className={`w-full flex items-center px-3 py-2 text-xs text-left rounded-md language-option ${
                  language === 'en' 
                    ? 'bg-primary text-white font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-base mr-2">🇺🇸</span>
                <span className="flex-grow">English</span>
                {language === 'en' && <Check size={12} className="ml-auto" />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Icon only variant (for headers, tight spaces)
  if (variant === 'icon') {
    return (
      <div className="relative language-selector" ref={dropdownRef}>
        <button
          onClick={handleToggleDropdown}
          className={`flex items-center px-3 py-2 rounded-lg text-white transition-all duration-300 hover:bg-white/20 hover:scale-105 ${animate ? 'animate-pulse' : ''} ${className}`}
          aria-label={t('language.switch')}
          title={t('language.switch')}
        >
          <Globe size={18} className="mr-2 transition-transform duration-300 hover:rotate-12" />
          <span className="text-sm font-medium">{t('language.label')}</span>
        </button>
        
        {isOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg overflow-hidden z-50 border border-gray-100 animate-fadeIn">
            <div className="p-1">
              <button
                onClick={() => handleLanguageChange('es')}
                className={`w-full flex items-center px-4 py-3 text-sm text-left rounded-md language-option ${
                  language === 'es' 
                    ? 'bg-primary text-white font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-lg mr-2">🇪🇸</span>
                <span className="flex-grow">{t('language.es', 'Español')}</span>
                {language === 'es' && <Check size={16} className="ml-auto" />}
              </button>
              
              <button
                onClick={() => handleLanguageChange('en')}
                className={`w-full flex items-center px-4 py-3 text-sm text-left rounded-md language-option ${
                  language === 'en' 
                    ? 'bg-primary text-white font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-lg mr-2">🇺🇸</span>
                <span className="flex-grow">{t('language.en', 'English')}</span>
                {language === 'en' && <Check size={16} className="ml-auto" />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Minimal variant (compact button with current language)
  if (variant === 'minimal') {
    return (
      <div className="relative language-selector" ref={dropdownRef}>
        <button
          onClick={handleToggleDropdown}
          className={`flex items-center px-3 py-2 rounded-lg transition-all duration-300 
            ${animate ? 'animate-pulse' : ''} 
            ${className || 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          aria-label={t('language.switch')}
        >
          <Globe size={16} className={`mr-2 ${animate ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">{t('language.label')}</span>
          <ChevronDown size={14} className={`ml-1 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg overflow-hidden z-50 border border-gray-100 animate-fadeIn">
            <div className="p-1">
              <button
                onClick={() => handleLanguageChange('es')}
                className={`w-full flex items-center px-4 py-3 text-sm text-left rounded-md language-option ${
                  language === 'es' 
                    ? 'bg-primary text-white font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-lg mr-2">🇪🇸</span>
                <span className="flex-grow">Español</span>
                {language === 'es' && <Check size={16} className="ml-auto" />}
              </button>
              
              <button
                onClick={() => handleLanguageChange('en')}
                className={`w-full flex items-center px-4 py-3 text-sm text-left rounded-md language-option ${
                  language === 'en' 
                    ? 'bg-primary text-white font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="language-flag text-lg mr-2">🇺🇸</span>
                <span className="flex-grow">English</span>
                {language === 'en' && <Check size={16} className="ml-auto" />}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default variant: Full (for larger areas)
  return (
    <div className={`language-selector ${className}`} ref={dropdownRef}>
      <button
        onClick={handleToggleDropdown} 
        className={`flex items-center justify-between w-full px-4 py-2 bg-white text-gray-800 rounded-lg shadow-sm hover:shadow transition-all duration-300 ${animate ? 'animate-pulse' : ''}`}
      >
        <div className="flex items-center">
          <Globe size={18} className={`text-primary mr-2 ${animate ? 'animate-spin' : ''}`} />
          <span className="font-medium text-sm">
            {language === 'es' ? 
              <span className="flex items-center">🇪🇸 Español</span> : 
              <span className="flex items-center">🇺🇸 English</span>
            }
          </span>
        </div>
        <ChevronDown size={16} className={`text-gray-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute mt-2 w-full bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-100 animate-fadeIn">
          <div className="p-1">
            <button
              onClick={() => handleLanguageChange('es')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-md language-option ${
                language === 'es' 
                  ? 'bg-primary text-white font-medium' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="language-flag text-lg mr-3">🇪🇸</span>
              <span className="flex-grow text-sm">Español</span>
              {language === 'es' && <Check size={16} className="ml-auto" />}
            </button>
            
            <button
              onClick={() => handleLanguageChange('en')}
              className={`w-full flex items-center px-4 py-3 text-left rounded-md language-option ${
                language === 'en' 
                  ? 'bg-primary text-white font-medium' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="language-flag text-lg mr-3">🇺🇸</span>
              <span className="flex-grow text-sm">English</span>
              {language === 'en' && <Check size={16} className="ml-auto" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;